import React, { useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { theme } from 'antd';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useVenue, useVenueShows, useVenueShowComments, useVenueShowMedia, useVenueBands } from '../../api/queries';
import {
  ProfileLayout,
  UpcomingShowsSection,
  ShowPhotosSection,
  ShowCommentsSection,
  PeopleListSection,
} from '../../components/Profile';
import ProfileSection from '../../components/Profile/sections/ProfileSection';
import type { ActionSheetItem } from '../../components/SpecActionSheet/SpecActionSheet';
import type { ShowListingProps } from '../../components/DirectoryListings/ShowListing/ShowListing';
import type { PersonItem } from '../../components/Profile';
import type { CommentProps } from '../../components/Comment/Comment';
import type { PhotoMetadata } from '../../components/PhotoGallery/PhotoOverlayInfo';
import { LocationMarkerIcon, ChatIcon } from '../../components/icons';
import { UserTag } from '../../components/UserTag/UserTag';
import type { UserType } from '../../components/UserTag/UserTag';
import UserTypeInfo from '../../components/UserTypeInfo/UserTypeInfo';
import AccentButton from '../../components/AccentButton/AccentButton';
import { useHeader } from '../../components/Layout/MainLayout';
import SpecAlert from '../../components/SpecAlert/SpecAlert';
import { useShare } from '../../hooks/useShare';
import { getShowDateTime } from '../../utils/showDateTime';

dayjs.extend(isSameOrAfter);

const VenueDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { data: venue, isLoading, error } = useVenue(id);
  const { data: venueShows } = useVenueShows(id);
  const { data: venueShowComments } = useVenueShowComments(id);
  const { data: venueShowMedia } = useVenueShowMedia(id);
  const { data: venueBands } = useVenueBands(id);

  const { setRightExtra } = useHeader();
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [openInfoType, setOpenInfoType] = useState<UserType | null>(null);
  const toastKeyRef = useRef(0);
  const showToast = useCallback((msg: string) => { toastKeyRef.current += 1; setToastMessage(msg); }, []);
  const share = useShare(showToast);

  // Format date to "Day Month Date" format (e.g., "Wed Sep 17")
  const formatDate = (dateString: string) => {
    // Parse date parts manually to avoid timezone issues with date-only strings
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format timestamp to "Mon Day Time" format (e.g., "Sep 16 3:29 PM")
  const formatTimestamp = (timestamp: string | { seconds: number } | { _seconds: number } | undefined) => {
    if (!timestamp) return '';

    // Handle Firestore Timestamp object (with seconds or _seconds)
    if (typeof timestamp === 'object') {
      const seconds = 'seconds' in timestamp ? timestamp.seconds : '_seconds' in timestamp ? (timestamp as { _seconds: number })._seconds : null;
      if (seconds) {
        return dayjs.unix(seconds).format('MMM D h:mm A');
      }
      return '';
    }

    // Handle ISO string or other date string
    const parsed = dayjs(timestamp);
    if (!parsed.isValid()) return '';
    return parsed.format('MMM D h:mm A');
  };

  // Extract display name from what might be an email (use part before @)
  const formatUserName = (name: string | undefined) => {
    if (!name) return 'Anonymous';
    if (name.includes('@')) return name.split('@')[0];
    return name;
  };

  // Filter venue shows into upcoming (today and future) and sort by date
  const today = dayjs().startOf('day');

  const allShowsTransformed = (venueShows || [])
    .map(show => ({
      id: show.id,
      bandName: show.band_name,
      date: formatDate(show.date),
      rawDate: show.date,
      venue: venue?.name || show.venue_name || 'TBA',
      time: show.time,
      description: show.description,
      imageUrl: show.band_photo_url,
    }))
    .sort((a, b) => getShowDateTime(a.rawDate, a.time) - getShowDateTime(b.rawDate, b.time));

  const upcomingShows = allShowsTransformed
    .filter(show => dayjs(show.rawDate).startOf('day').isSameOrAfter(today))
    .map(({ rawDate, ...show }) => show);

  // Only show upcoming shows in the main section
  const showsToDisplay = upcomingShows;

  // Transform venue bands to PersonItem format
  const pastBands: PersonItem[] = (venueBands || []).map(band => ({
    name: band.name,
    avatarUrl: band.photo_url,
    avatarShape: 'rounded' as const,
    route: `/bands/${band.id}`,
  }));

  // Transform show comments to CommentProps format
  const comments: Omit<CommentProps, 'className'>[] = (venueShowComments || []).map(comment => {
    const rawUserId = comment.user_id || comment.created_by_id;
    const userId = rawUserId && rawUserId !== 'system' ? rawUserId : undefined;
    return {
      userName: formatUserName(comment.user_name),
      avatarUrl: comment.user_avatar_url,
      timestamp: formatTimestamp(comment.timestamp || comment.created_at || ''),
      userTags: (comment.user_tags || []) as ('fan' | 'musician' | 'showContributor')[],
      text: comment.comment,
      userProfileUrl: userId ? `/fans/${userId}` : undefined,
      onUserClick: userId ? () => navigate(`/fans/${userId}`) : undefined,
      showEventInfo: !!(comment.band_name && comment.show_date && comment.venue_name),
      eventInfo: (comment.band_name && comment.show_date && comment.venue_name) ? {
        artistName: comment.band_name,
        artistAvatarUrl: comment.band_photo_url,
        date: formatDate(comment.show_date),
        venue: comment.venue_name,
      } : undefined,
    };
  });

  // Transform show media to photos array and metadata
  const photos: string[] = (venueShowMedia || []).map(media => media.url);
  const photoMetadata: PhotoMetadata[] = (venueShowMedia || []).map(media => {
    const rawUserId = media.user_id || media.created_by_id;
    const userId = rawUserId && rawUserId !== 'system' ? rawUserId : undefined;
    return {
      userName: formatUserName(media.user_name),
      userAvatarUrl: media.user_avatar_url,
      timestamp: formatTimestamp(media.timestamp || media.created_at || ''),
      userTags: (media.user_tags || []) as ('fan' | 'musician' | 'showContributor')[],
      userProfileUrl: userId ? `/fans/${userId}` : undefined,
      eventInfo: (media.band_name && media.show_date && media.venue_name) ? {
        artistName: media.band_name,
        artistAvatarUrl: media.band_photo_url,
        date: formatDate(media.show_date),
        venue: media.venue_name,
      } : undefined,
    };
  });

  const actionItems: ActionSheetItem[] = venue ? [
    { key: 'share', label: 'Share', onClick: () => share({ title: venue.name, entityType: 'venue', entityId: id }) },
    { key: 'copy-link', label: 'Copy Link', onClick: () => { navigator.clipboard?.writeText(window.location.href).catch(() => {}); showToast('Link Copied'); } },
    { key: 'qr-code', label: 'QR Code', onClick: () => navigate(`/venue/${id}/qr`) },
  ] : [];

  React.useEffect(() => {
    if (venue?.address) {
      setRightExtra(
        <AccentButton size="small" shape="round" onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.address)}`, '_blank')}>
          Get Directions
        </AccentButton>
      );
    }
    return () => {
      setRightExtra(undefined);
    };
  }, [setRightExtra, venue?.address]);

  if (isLoading) {
    return (
      <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
        Loading venue...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorError }}>
        Error loading venue: {error.message}
      </div>
    );
  }

  if (!venue) {
    return (
      <div style={{ padding: 24, fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
        Venue not found.
      </div>
    );
  }

  return (
    <ProfileLayout
      profileType="venue"
      heroVariant={venue.photo_url ? "blurred" : "none"}
      heroImageUrl={venue.photo_url}
      actionSheetItems={actionItems}
    >
      <div className="band-identity">
        <div className="band-identity__avatar">
          {venue.photo_url ? (
            <img
              src={venue.photo_url}
              alt={venue.name}
              className="band-identity__avatar-img band-identity__avatar-img--circle"
            />
          ) : (
            <div className="band-identity__avatar-placeholder band-identity__avatar-placeholder--circle">
              <LocationMarkerIcon size="xl" tone="lightGrey" />
            </div>
          )}
        </div>
        <div className="band-identity__info">
          <h1 className="band-identity__name">{venue.name}</h1>
          {venue.address && (
            <p className="band-identity__subtitle">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                {venue.address}
              </a>
            </p>
          )}
          <div className="band-identity__badges">
            <UserTag type="venue" onClick={() => setOpenInfoType('venue')} />
          </div>
        </div>
        <UserTypeInfo type="venue" open={openInfoType === 'venue'} onClose={() => setOpenInfoType(null)} />
      </div>

      {/* Shows */}
      <UpcomingShowsSection
        shows={showsToDisplay}
        allShows={allShowsTransformed}
        showAddButton
        addShowIcon={<ChatIcon size="s" tone="black" />}
        onAddShow={() => navigate('/add-show')}
      />

      {/* Show Photos */}
      {photos.length > 0 ? (
        <ShowPhotosSection
          photos={photos}
          photoMetadata={photoMetadata}
          subtitle="Photos from the venue's past shows"
        />
      ) : (
        <ProfileSection title="Show Photos" subtitle="Photos from the venue's past shows">
          <div className="view-all-drawer__empty">No photos yet</div>
        </ProfileSection>
      )}

      {/* Show Comments */}
      {comments.length > 0 ? (
        <ShowCommentsSection
          comments={comments}
          showEventInfo
        />
      ) : (
        <ProfileSection title="Show Comments">
          <div className="view-all-drawer__empty">No comments yet</div>
        </ProfileSection>
      )}

      {/* Past Bands */}
      {pastBands.length > 0 ? (
        <PeopleListSection
          title="Past Bands"
          subtitle={`Bands that have played at ${venue.name}`}
          people={pastBands}
          avatarShape="rounded"
        />
      ) : (
        <ProfileSection title="Past Bands" subtitle={`Bands that have played at ${venue.name}`}>
          <div className="view-all-drawer__empty">No bands yet</div>
        </ProfileSection>
      )}

      <SpecAlert key={toastKeyRef.current} message={toastMessage} onDone={() => setToastMessage(null)} />
    </ProfileLayout>
  );
};

export default VenueDetail;
